import React, { useState, useEffect, useCallback } from "react";
import { X, Download, Upload, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PdfIcon from "../../Images/pdf_icon.svg";
import UploadIcon from "../../Images/upload-cloud-01.svg";
import { API_URL } from "../../config/api";
import { uploadDocumentToCloudinary } from "../../config/cloudinary";
import ToastMessage from "../../config/ToastMessage";

export default function AssignmentSub({
  isOpen,
  onClose,
  task,
  type,
  scheduleId,
}) {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });

  const showToast = useCallback((message, toastType = "success") => {
    setToast({ show: true, type: toastType, message });
  }, []);

  const hideToast = useCallback(() => {
    setToast({ show: false, type: "", message: "" });
  }, []);

  // Get the appropriate ID based on task type
  const getTaskId = () => {
    if (!task) return null;
    if (type === "assignment") return task.assignment_id;
    if (type === "lab") return task.lab_id;
    if (type === "demo") return task.demo_id;
    if (type === "quiz") return task.quiz_id;
    return null;
  };

  const taskId = getTaskId();

  // Fetch task details based on type
  const fetchTaskDetails = useCallback(async () => {
    if (!taskId || !type) return;

    // For quiz, redirect directly instead of showing modal
    if (type === "quiz") {
      navigate(
        `/myprograms/quiz_attend?quiz_id=${taskId}&schedule_id=${scheduleId}`,
      );
      onClose();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        showToast("Authentication required. Please log in again.", "error");
        return;
      }

      let endpoint = "";
      if (type === "assignment") endpoint = `${API_URL}/assignments/${taskId}`;
      if (type === "lab") endpoint = `${API_URL}/labs/${taskId}`;
      if (type === "demo") endpoint = `${API_URL}/demo/${taskId}`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success || response.data) {
        setTaskDetails(response.data.data || response.data);
      } else {
        showToast(`Failed to load ${type} details`, "error");
      }
    } catch (error) {
      console.error(`Error fetching ${type} details:`, error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        `Failed to load ${type} details. Please try again.`;
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }, [taskId, type, showToast, navigate, scheduleId, onClose]);

  useEffect(() => {
    if (isOpen && taskId && type) {
      fetchTaskDetails();
    }
  }, [isOpen, taskId, type, fetchTaskDetails]);

  useEffect(() => {
    if (isOpen) {
      setToast({ show: false, type: "", message: "" });
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = (taskDetails?.max_file_size_mb || 4) * 1024 * 1024;
    const supportedTypes = taskDetails?.file_supported_types || "pdf";
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!supportedTypes.toLowerCase().includes(fileExtension)) {
      showToast(
        `Please upload a ${supportedTypes.toUpperCase()} file.`,
        "error",
      );
      e.target.value = "";
      return;
    }

    if (file.size > maxSize) {
      showToast(
        `File size exceeds ${taskDetails?.max_file_size_mb || 4} MB limit.`,
        "error",
      );
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      showToast("Please select a file before submitting.", "error");
      return;
    }

    if (!taskId || !scheduleId) {
      showToast(`Missing ${type} or schedule information.`, "error");
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        showToast("Authentication required. Please log in again.", "error");
        setSubmitting(false);
        return;
      }

      // ✅ Step 1: Upload to Azure
      const formData = new FormData();
      formData.append("file", selectedFile);

      const uploadResponse = await fetch(`${API_URL}/azure-upload/pdf-upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error("Azure upload failed");
      }

      const uploadData = await uploadResponse.json();

      // ✅ Use Azure file URL
      const fileUrl = uploadData.url;

      if (!fileUrl) {
        throw new Error("File upload failed");
      }

      // ✅ Step 2: Submit assignment/lab/demo
      let endpoint = "";
      let payload = {
        schedule_id: scheduleId,
        file_url: fileUrl,
      };

      if (type === "assignment") {
        endpoint = `${API_URL}/assignments/submit`;
        payload.assignment_id = taskId;
      }

      if (type === "lab") {
        endpoint = `${API_URL}/labs/submit`;
        payload.lab_id = taskId;
      }

      if (type === "demo") {
        endpoint = `${API_URL}/demo/submit`;
        payload.demo_id = taskId;
      }

      const response = await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      showToast(
        response.data?.message ||
          `${type.charAt(0).toUpperCase() + type.slice(1)} submitted successfully!`,
        "success",
      );

      setTimeout(() => {
        setSelectedFile(null);
        setTaskDetails(null);
        onClose();
      }, 2000);
    } catch (error) {
      console.error(`Error submitting ${type}:`, error);

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        `Failed to submit ${type}. Please try again.`;

      showToast(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadReference = async () => {
    if (!taskDetails?.file_url) {
      showToast("No reference file available.", "error");
      return;
    }

    try {
      const blobPath = taskDetails.file_url.split("/skillmd-dev-container/")[1];

      const response = await fetch(
        `${API_URL}/azure-upload/download/${blobPath}`,
      );

      if (!response.ok) {
        throw new Error("Failed to generate download link");
      }

      const data = await response.json();

      const link = document.createElement("a");
      link.href = data.download_url;
      link.target = "_blank";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download error:", error);
      showToast("Failed to download file.", "error");
    }
  };

  const handleClose = () => {
    if (submitting) return;

    setSelectedFile(null);
    setTaskDetails(null);

    // ✅ ADD THIS LINE
    setToast({ show: false, type: "", message: "" });

    onClose();
  };

  const calculateDeadline = (createdAt, days = 7) => {
    try {
      return new Date(
        new Date(createdAt).getTime() + days * 24 * 60 * 60 * 1000,
      ).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch {
      return "Not set";
    }
  };

  const getTitle = () => {
    if (type === "assignment") return "Assignment";
    if (type === "lab") return "Lab";
    if (type === "demo") return "Demo";
    return "Task";
  };

  if (!isOpen) return null;

  // Don't show modal for quiz type
  if (type === "quiz") return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="w-full max-w-2xl bg-white rounded-lg border border-gray-200 p-6 sm:p-8">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">
              Loading {type} details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const details = taskDetails || task;
  // ----------------------------------------------------
  // Get current schedule using scheduleId
  // ----------------------------------------------------
  const getCurrentSchedule = () => {
    if (!details || !scheduleId) return null;

    let schedules = [];

    if (type === "demo") {
      schedules = details?.demo_schedules || [];
    } else if (type === "lab") {
      schedules = details?.lab_schedules || [];
    } else if (type === "assignment") {
      schedules = details?.assignment_schedules || [];
    }

    return schedules.find((s) => Number(s.schedule_id) === Number(scheduleId));
  };

  const currentSchedule = getCurrentSchedule();

  const submissionDeadline = (() => {
    if (currentSchedule?.end_date) {
      try {
        return new Date(currentSchedule.end_date).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });
      } catch {
        return "Not set";
      }
    }

    return details?.deadline || "Not set";
  })();

  const gradePostingDate = (() => {
    if (currentSchedule?.grade_posting_date) {
      try {
        return new Date(currentSchedule.grade_posting_date).toLocaleDateString(
          "en-US",
          {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          },
        );
      } catch {
        return "Not set";
      }
    }

    return details?.grade_posting_date || "Not set";
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      {toast.show && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={hideToast}
        />
      )}

      <div className="w-full max-w-2xl bg-white rounded-lg border border-gray-200 p-4 sm:p-6 md:p-8 relative my-4">
        <button
          onClick={handleClose}
          disabled={submitting}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>

        <div className="mb-6 pr-8">
          <div className="text-left sm:text-right mb-4 sm:mb-6">
            <span className="text-stone-900 text-sm sm:text-base font-normal block sm:inline">
              Submission Last Date:{" "}
            </span>
            <span className="text-zinc-800 text-base sm:text-lg font-semibold">
              {submissionDeadline}
            </span>
          </div>

          <h1 className="text-zinc-800 text-lg sm:text-xl font-bold mb-4">
            {details?.title || getTitle()}
          </h1>

          <div className="mb-6">
            <span className="text-zinc-800 text-sm font-bold">
              Instructions:
            </span>
            <p className="text-zinc-600 text-sm mt-1 leading-relaxed">
              {details?.instructions ||
                details?.summary ||
                details?.description ||
                `Complete the ${type} and submit as per the guidelines.`}
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {details?.file_url && (
            <div className="w-full lg:w-64 bg-white rounded-lg border border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 relative flex-shrink-0">
                  <img src={PdfIcon} alt="File icon" className="w-11 h-11" />
                  <div className="text-white text-xs font-bold absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    {details?.file_supported_types?.toUpperCase() || "PDF"}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-600 text-xs leading-4 break-words">
                    {getTitle()}
                    <br />
                    {details?.reference_file ||
                      details?.file_url?.split("/").pop()?.substring(0, 30) ||
                      "reference.pdf"}
                  </p>
                </div>
              </div>

              <button
                onClick={handleDownloadReference}
                className="flex items-center gap-2 text-cyan-500 text-xs font-medium underline hover:text-cyan-600 transition-colors"
              >
                <Download className="w-4 h-4 text-teal-400" />
                Download Reference
              </button>
            </div>
          )}

          <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4">
            <div className="mb-3">
              <span className="text-zinc-600 text-sm block sm:inline">
                Grade Posting Date:{" "}
              </span>
              <span className="text-zinc-800 text-sm sm:text-base font-semibold">
                {gradePostingDate}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
              <div>
                <span className="text-zinc-600 text-sm">Maximum Points: </span>
                <span className="text-stone-900 text-sm font-semibold">
                  {details?.max_points || "50"}
                </span>
              </div>
              <div>
                <span className="text-zinc-600 text-sm">Minimum Points: </span>
                <span className="text-stone-900 text-sm font-bold">
                  {details?.min_points || "15"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 mb-6"></div>

        <div className="mb-6">
          <div className="flex items-center gap-1 mb-2">
            <label
              htmlFor="fileUpload"
              className="text-zinc-800 text-sm font-bold"
            >
              Upload your completed {getTitle()}
            </label>
            <span className="text-red-600 text-sm font-bold">*</span>
          </div>

          <div className="relative">
            <input
              type="file"
              accept={`.${details?.file_supported_types || "pdf"}`}
              onChange={handleFileChange}
              className="hidden"
              id="fileUpload"
              disabled={submitting}
              aria-label={`Upload ${type} file`}
            />
            <label
              htmlFor="fileUpload"
              className={`min-h-[96px] px-3 py-4 bg-white rounded-lg shadow-sm border border-zinc-200 flex flex-col justify-center items-center gap-2 ${
                submitting
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:bg-gray-50"
              } transition-colors`}
            >
              <img src={UploadIcon} alt="Upload" className="w-8 h-8" />
              <div className="text-center px-2">
                {selectedFile ? (
                  <span className="text-zinc-700 text-sm font-medium break-all">
                    {selectedFile.name}
                  </span>
                ) : (
                  <>
                    <span className="text-zinc-400 text-sm">
                      Upload your File{" "}
                    </span>
                    <span className="text-blue-500 text-sm underline">
                      Browse
                    </span>
                  </>
                )}
              </div>
            </label>
          </div>

          <div className="flex items-start gap-1 mt-2">
            <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
            <span className="text-zinc-400 text-xs">
              Max File Size: {details?.max_file_size_mb || 4} MB, File Formats:{" "}
              {details?.file_supported_types?.toUpperCase() || "PDF"}
            </span>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedFile}
            className={`w-full sm:w-48 h-11 px-6 py-3 rounded-md flex justify-center items-center transition-all ${
              submitting || !selectedFile
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-teal-400 hover:bg-teal-500 active:bg-teal-600"
            }`}
            aria-label={submitting ? `Submitting ${type}` : `Submit ${type}`}
          >
            <span className="text-white text-base font-semibold">
              {submitting ? "Submitting..." : "Submit"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
